import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQueryClient, useMutation } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import { AxiosError } from 'axios';

import Box from '@/components/common/Box';
import Spacer from '@/components/common/Spacer';
import Text from '@/components/common/Text';
import Button from '@/components/common/Button';
import Footer from '@/components/common/Footer';
import useMonthlyBillDetail from '@/hooks/useMonthlyBillDetail';
import useCompanyInfo from '@/hooks/useCompanyInfo';
import { dateFormat } from '@/utils/convertDateFormat';
import { Theme } from '@/styles/Theme';
import { ROUTER } from '@/constants/constants';
import { QUERY_KEY } from '@/constants/queryKey';
import { PageCenter } from '@/styles/common';
import { confirmMonthlyBillPayment } from '@/apis/company';
import { postImg } from '@/apis/img';
import type { ErrorResponse } from '@/types/client.types';

import * as styled from './BillPaymentPage.styles';

declare global {
  interface Window {
    PaypleCpayAuthCheck?: any;
  }
}

const BillPaymentPage = () => {
  const { billId } = useParams<{ billId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { data: billDetailData, isLoading } = useMonthlyBillDetail(billId ? Number(billId) : null);
  const { data: companyData } = useCompanyInfo();

  const bill = billDetailData?.data?.bill;
  const { companyId, companyName, phone, email } = companyData?.data || {};

  // POSTPAID payment confirmation state
  const [note, setNote] = useState<string>('');
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [isUploadingImage, setIsUploadingImage] = useState(false);

  useEffect(() => {
    if (!billId) {
      navigate(ROUTER.DASHBOARD, { replace: true });
    }
  }, [billId, navigate]);

  const handlePayment = () => {
    if (!bill || !companyId || !companyName || !phone || !email) {
      toast.error('결제 정보를 불러올 수 없습니다.');
      return;
    }

    // PREPAID: Use Payple payment gateway
    if (bill.payment_method === 'prepaid') {
      if (!window.PaypleCpayAuthCheck) {
        toast.error('결제 시스템을 사용할 수 없습니다.');
        return;
      }

      const pay_oid = `prepaid_renewal_${companyId}_${Date.now()}`;
      const data = {
        clientKey: process.env.REACT_APP_PAYPLE_CLIENTKEY,
        PCD_PAY_TYPE: 'card',
        PCD_PAY_WORK: 'PAY',
        PCD_CARD_VER: '01',
        PCD_PAY_GOODS: `블루멘토 ${bill.billing_period_start.substring(0, 7)} 이용요금`,
        PCD_PAY_TOTAL: bill.total_amount.toString(),
        PCD_PAY_OID: pay_oid,
        PCD_PAYER_NO: companyId.toString(),
        PCD_PAYER_NAME: companyName,
        PCD_PAYER_HP: phone,
        PCD_PAYER_EMAIL: email,
        PCD_RST_URL: '/example',
        callbackFunction: (response: any) => {
          const { PCD_PAY_MSG, PCD_PAY_RST } = response;
          alert(PCD_PAY_MSG);

          if (PCD_PAY_RST === 'success') {
            // Backend will handle the payment result via payResult endpoint
            // The pay_oid format `prepaid_renewal_` will be recognized by backend
            toast.success('결제가 완료되었습니다.');
            queryClient.invalidateQueries([QUERY_KEY.MONTHLY_BILLS]);
            queryClient.invalidateQueries([QUERY_KEY.MONTHLY_BILL_DETAIL]);
            queryClient.invalidateQueries([QUERY_KEY.COMPANY_INFO]);
            navigate(ROUTER.DASHBOARD, { replace: true });
          }
        },
      };

      window.PaypleCpayAuthCheck(data);
      return;
    }

    // POSTPAID: Manual payment confirmation - handled by handlePostpaidConfirm
  };

  // POSTPAID: Handle image upload
  const handleImageChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files || e.target.files.length === 0) return;

    const file = e.target.files[0];
    const formData = new FormData();
    formData.append('files', file);

    setIsUploadingImage(true);
    try {
      const response = await postImg(formData);
      if (response.data && response.data.length > 0) {
        setImageUrl(response.data[0]);
        toast.success('이미지가 업로드되었습니다.');
      }
    } catch (error) {
      if (error instanceof AxiosError) {
        const axiosError = error as AxiosError<ErrorResponse>;
        toast.error(axiosError.response?.data.message || '이미지 업로드에 실패했습니다.');
      } else {
        toast.error('이미지 업로드에 실패했습니다.');
      }
    } finally {
      setIsUploadingImage(false);
    }
  };

  // POSTPAID: Handle payment confirmation
  const { mutate: confirmPaymentMutate, isLoading: isConfirming } = useMutation({
    mutationFn: (data: { note?: string; image_url?: string }) =>
      confirmMonthlyBillPayment(billId ? Number(billId) : 0, data),
    onSuccess: () => {
      toast.success('결제 확인 요청이 제출되었습니다.');
      queryClient.invalidateQueries([QUERY_KEY.MONTHLY_BILLS]);
      queryClient.invalidateQueries([QUERY_KEY.MONTHLY_BILL_DETAIL]);
      navigate(ROUTER.DASHBOARD);
    },
    onError: (error: AxiosError<ErrorResponse>) => {
      toast.error(error.response?.data.message || '결제 확인 요청에 실패했습니다.');
    },
  });

  const handlePostpaidConfirm = () => {
    if (!billId) {
      toast.error('청구서 ID가 없습니다.');
      return;
    }

    confirmPaymentMutate({
      note: note.trim() || undefined,
      image_url: imageUrl || undefined,
    });
  };

  if (isLoading) {
    return (
      <PageCenter>
        <Box title='청구서 결제'>
          <Text fontSize='1rem' color={Theme.colors.G_1}>
            로딩 중...
          </Text>
        </Box>
      </PageCenter>
    );
  }

  if (!bill) {
    return (
      <PageCenter>
        <Box title='청구서 결제'>
          <Text fontSize='1rem' color={Theme.colors.WARNING_RED}>
            청구서를 찾을 수 없습니다.
          </Text>
          <Spacer y='1rem' />
          <Button
            backgroundColor={Theme.colors.G_5}
            color={Theme.colors.BLACK}
            onClick={() => navigate(ROUTER.DASHBOARD)}
          >
            대시보드로 이동
          </Button>
        </Box>
      </PageCenter>
    );
  }

  const canPay = bill.status === 'sent' || bill.status === 'failed';
  const canConfirmPostpaid = bill.status === 'sent' || bill.status === 'overdue';
  const isPrepaid = bill.payment_method === 'prepaid';
  const isPostpaid = bill.payment_method === 'postpaid';

  return (
    <>
      <PageCenter>
        <Box title='청구서 결제'>
          <styled.InfoSection>
            <styled.InfoRow>
              <styled.InfoLabel>청구 기간:</styled.InfoLabel>
              <styled.InfoValue>
                {dateFormat(bill.billing_period_start)} ~ {dateFormat(bill.billing_period_end)}
              </styled.InfoValue>
            </styled.InfoRow>
            <styled.InfoRow>
              <styled.InfoLabel>결제 방식:</styled.InfoLabel>
              <styled.InfoValue>{isPrepaid ? '선불' : '후불'}</styled.InfoValue>
            </styled.InfoRow>
            <styled.InfoRow>
              <styled.InfoLabel>작업자 수:</styled.InfoLabel>
              <styled.InfoValue>{bill.worker_count}명</styled.InfoValue>
            </styled.InfoRow>
            <styled.InfoRow>
              <styled.InfoLabel>총 금액:</styled.InfoLabel>
              <styled.InfoValue>
                <Text fontSize='1.125rem' fontWeight={700}>
                  {bill.total_amount.toLocaleString('ko-KR')}원
                </Text>
              </styled.InfoValue>
            </styled.InfoRow>
            {bill.sent_at && (
              <styled.InfoRow>
                <styled.InfoLabel>발송일:</styled.InfoLabel>
                <styled.InfoValue>{dateFormat(bill.sent_at)}</styled.InfoValue>
              </styled.InfoRow>
            )}
          </styled.InfoSection>

          <Spacer y='1.5rem' />

          <styled.WorkersSection>
            <Text fontSize='1.125rem' fontWeight={600} margin='0 0 0.75rem 0'>
              작업자별 내역
            </Text>
            <styled.WorkersTable>
              <styled.WorkersThead>
                <styled.WorkersTr>
                  <styled.WorkersTh>작업자 이름</styled.WorkersTh>
                  <styled.WorkersTh>단가</styled.WorkersTh>
                  <styled.WorkersTh>활성 기간</styled.WorkersTh>
                  <styled.WorkersTh>금액</styled.WorkersTh>
                </styled.WorkersTr>
              </styled.WorkersThead>
              <styled.WorkersTbody>
                {bill.workers && bill.workers.length > 0 ? (
                  bill.workers.map((worker) => (
                    <styled.WorkersTr key={worker.id}>
                      <styled.WorkersTd>{worker.worker_name || '-'}</styled.WorkersTd>
                      <styled.WorkersTd>
                        {worker.price_per_worker.toLocaleString('ko-KR')}원
                      </styled.WorkersTd>
                      <styled.WorkersTd>
                        {dateFormat(worker.active_from)}
                        {worker.active_to && ` ~ ${dateFormat(worker.active_to)}`}
                      </styled.WorkersTd>
                      <styled.WorkersTd>
                        {worker.amount.toLocaleString('ko-KR')}원
                      </styled.WorkersTd>
                    </styled.WorkersTr>
                  ))
                ) : (
                  <styled.WorkersTr>
                    <styled.WorkersTd colSpan={4}>
                      <Text fontSize='0.875rem' color={Theme.colors.G_1}>
                        작업자 정보가 없습니다.
                      </Text>
                    </styled.WorkersTd>
                  </styled.WorkersTr>
                )}
              </styled.WorkersTbody>
            </styled.WorkersTable>
          </styled.WorkersSection>

          {isPostpaid && canConfirmPostpaid && (
            <>
              <Spacer y='1.5rem' />
              <styled.ConfirmationSection>
                <Text fontSize='1.125rem' fontWeight={600} margin='0 0 1rem 0'>
                  결제 확인
                </Text>
                <styled.ConfirmationLabel htmlFor='payment-note'>
                  메모 (선택사항)
                </styled.ConfirmationLabel>
                <styled.ConfirmationTextarea
                  id='payment-note'
                  placeholder='결제 관련 메모를 입력해주세요.'
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  maxLength={500}
                />
                <styled.FileInputWrapper>
                  <styled.ConfirmationLabel htmlFor='payment-image'>
                    결제 증빙 이미지 (선택사항)
                  </styled.ConfirmationLabel>
                  <styled.FileInputLabel htmlFor='payment-image'>
                    {imageUrl ? '이미지 변경' : '이미지 선택'}
                  </styled.FileInputLabel>
                  <styled.FileInput
                    id='payment-image'
                    type='file'
                    accept='image/*'
                    onChange={handleImageChange}
                    disabled={isUploadingImage}
                  />
                  {imageUrl && (
                    <styled.ImagePreview>
                      <styled.ImagePreviewImg src={imageUrl} alt='Payment proof' />
                      <styled.ImageRemoveButton
                        type='button'
                        onClick={() => setImageUrl(null)}
                      >
                        삭제
                      </styled.ImageRemoveButton>
                    </styled.ImagePreview>
                  )}
                </styled.FileInputWrapper>
              </styled.ConfirmationSection>
            </>
          )}

          <Spacer y='2rem' />

          <styled.ButtonWrapper>
            {canPay && isPrepaid && (
              <Button
                backgroundColor={Theme.colors.B_3}
                color={Theme.colors.WHITE}
                height='2.5rem'
                width='12rem'
                onClick={handlePayment}
              >
                결제하기
              </Button>
            )}
            {canConfirmPostpaid && isPostpaid && (
              <Button
                backgroundColor={Theme.colors.B_3}
                color={Theme.colors.WHITE}
                height='2.5rem'
                width='12rem'
                onClick={handlePostpaidConfirm}
                disabled={isConfirming || isUploadingImage}
              >
                {isConfirming ? '제출 중...' : '결제 확인 제출'}
              </Button>
            )}
            {!canPay && (
              <Text fontSize='1rem' color={Theme.colors.G_1}>
                {bill.status === 'paid'
                  ? '이미 결제가 완료된 청구서입니다.'
                  : '결제할 수 없는 청구서입니다.'}
              </Text>
            )}
            <Button
              backgroundColor={Theme.colors.G_5}
              color={Theme.colors.BLACK}
              height='2.5rem'
              width='12rem'
              onClick={() => navigate(ROUTER.DASHBOARD)}
            >
              돌아가기
            </Button>
          </styled.ButtonWrapper>
        </Box>
      </PageCenter>
      <Footer padding='2.5rem' />
    </>
  );
};

export default BillPaymentPage;


